import React, { Suspense, lazy, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useHistory, useLocation } from 'react-router';
import styled from 'styled-components';
import { Viewport } from '../../ViewportProvider';

const loadOverlay = () => import('./MobileSearchOverlay');
const MobileSearchOverlay = lazy(loadOverlay);

// Fills the screen the instant the overlay opens, even if its lazy chunk is
// still in flight (e.g. the tap lands before the prefetch settles on a slow
// connection) — otherwise fallback={null} shows nothing at all.
const OverlayFallback = styled.div`
  position: fixed;
  inset: 0;
  z-index: 10001;
  background: #ffffff;
`;

/** Below this width the header search input opens the full-screen overlay. */
export const MOBILE_SEARCH_BREAKPOINT = 992;

interface OpenOptions {
  initialKeyword?: string;
}

interface ContextValue {
  isOpen: boolean;
  open: (options?: OpenOptions) => void;
  close: () => void;
}

export const MobileSearchOverlayContext = React.createContext<ContextValue>({
  isOpen: false,
  open: () => undefined,
  close: () => undefined,
});

export const useMobileSearchOverlay = () => useContext(MobileSearchOverlayContext);

/**
 * Owns the single full-screen mobile search overlay.
 *
 * SearchProductComponent is mounted in four places (and two of them are live at
 * once on the mobile homepage: the header's collapsible row and the hero search
 * pill). Hoisting the open/close state here means there is exactly one overlay,
 * one autofocus and one scroll lock no matter which input the user tapped.
 */
export const MobileSearchOverlayProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const history = useHistory();

  const location = useLocation();

  const { width } = useContext(Viewport);

  const [isOpen, setIsOpen] = useState(false);

  const [initialKeyword, setInitialKeyword] = useState('');

  const isOpenRef = useRef(false);

  const openedKey = useRef<string | undefined>(undefined);

  const open = useCallback(
    (options?: OpenOptions) => {
      if (isOpenRef.current) return;
      isOpenRef.current = true;
      setInitialKeyword(options?.initialKeyword || '');
      setIsOpen(true);
      // Push a throwaway entry so Android's hardware back button and iOS's
      // edge-swipe dismiss the overlay instead of leaving the page.
      history.push({
        pathname: location.pathname,
        search: location.search,
        state: { ...((location.state as object) || {}), mobileSearch: true },
      });
      openedKey.current = history.location.key;
    },
    [history, location.pathname, location.search, location.state],
  );

  const close = useCallback(() => {
    isOpenRef.current = false;
    setIsOpen(false);
    if (openedKey.current !== undefined) {
      openedKey.current = undefined;
      history.goBack();
    }
  }, [history]);

  /**
   * Catches the one case close() can't: the overlay dismissed by something
   * that never calls it — the browser's native back gesture / hardware back
   * button. Guarded on openedKey so it never fires for a close() that already
   * ran (openedKey is cleared synchronously there).
   */
  useEffect(() => {
    if (isOpen && openedKey.current !== undefined && location.key !== openedKey.current) {
      openedKey.current = undefined;
      isOpenRef.current = false;
      setIsOpen(false);
    }
  }, [location.key]);

  /* Warm the chunk before the first tap: the overlay autofocuses its input on
     mount, and iOS only raises the keyboard if that happens inside the tap
     gesture — which a cold dynamic import would miss. */
  useEffect(() => {
    if (width < MOBILE_SEARCH_BREAKPOINT) {
      loadOverlay();
    }
  }, [width]);

  /** Rotating to landscape / resizing past the breakpoint hands back to the desktop dropdown. */
  useEffect(() => {
    if (isOpen && width >= MOBILE_SEARCH_BREAKPOINT) {
      close();
    }
  }, [width, isOpen, close]);

  const value = useMemo(() => ({ isOpen: isOpen, open: open, close: close }), [isOpen, open, close]);

  return (
    <MobileSearchOverlayContext.Provider value={value}>
      {children}
      {isOpen &&
        createPortal(
          <Suspense fallback={<OverlayFallback />}>
            <MobileSearchOverlay
              initialKeyword={initialKeyword}
              onClose={close}
              /** Consumes the dummy entry so back doesn't land on the overlay again. */
              navigate={(to) => {
                if (openedKey.current !== undefined) {
                  openedKey.current = undefined;
                  history.replace(to as any);
                } else {
                  history.push(to as any);
                }
              }}
            />
          </Suspense>,
          document.body,
        )}
    </MobileSearchOverlayContext.Provider>
  );
};

export default MobileSearchOverlayProvider;
